import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { mbeConfigService } from './mbe-config.service';
import {
  AtaSmsResponse,
  AwaitingTransaction,
  AwaitingTransactionAPI,
  AwtListFilterInput,
  GetAllAwaitingTransactionReq,
} from '../model/awaiting-transaction';
import { normalizeAwtDateFilter } from '../util/awt-date';

@Injectable({
  providedIn: 'root',
})
export class AwaitingTransactionService {
  private URL: string;
  headers: HttpHeaders;

  constructor(
    @Inject(mbeConfigService) private config,
    private httpClient: HttpClient
  ) {
    this.URL =
      config.REST_SERVER_HOST +
      config.REST_SERVER_url.find((restUrl) => restUrl.api === 'MBE').url;
    this.headers = new HttpHeaders().set('Content-Type', 'application/json');
  }

  private static handleHTTPError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    return throwError(error);
  }

  buildAwtRequest(input: AwtListFilterInput = {}): GetAllAwaitingTransactionReq {
    const memberNo = input.memberNo === '' ? '0' : input.memberNo ?? '0';
    const accNoSource = input.accNo ?? input.accountNumber;
    const accNo =
      accNoSource === '' || accNoSource === undefined || accNoSource === null
        ? '0'
        : accNoSource;

    return {
      pageIndex: input.pageIndex ?? 0,
      pageSize: input.pageSize ?? 1000,
      sortActive: input.sortActive || 'id',
      sort: input.sort || 'asc',
      status: input.status ?? 0,
      memberNo,
      accNo,
      bhfNo: input.bhfNo ?? 0,
      reportType: input.reportType ?? 0,
      remoteGID: input.remoteGID ?? input.remoteGlobalID ?? 0,
      statusDF: normalizeAwtDateFilter(
        input.statusDF ?? input.statusDateFrom
      ),
      statusDT: normalizeAwtDateFilter(input.statusDT ?? input.statusDateTo),
      creationDF: normalizeAwtDateFilter(
        input.creationDF ?? input.creationDateFrom
      ),
      creationDT: normalizeAwtDateFilter(
        input.creationDT ?? input.creationDateTo
      ),
    };
  }

  getAllAwaitingTrans(
    req: AwtListFilterInput | GetAllAwaitingTransactionReq = {}
  ): Observable<AwaitingTransactionAPI> {
    const headers = this.headers;
    const body = this.buildAwtRequest(req);
    return this.httpClient
      .post<AwaitingTransactionAPI>(`${this.URL}/awt/get`, body, { headers })
      .pipe(catchError(AwaitingTransactionService.handleHTTPError));
  }

  getAwaitingTrans(id: number): Observable<AwaitingTransaction> {
    const headers = this.headers;
    return this.httpClient
      .get<AwaitingTransaction>(`${this.URL}/awt/get/${id}`, { headers })
      .pipe(catchError(AwaitingTransactionService.handleHTTPError));
  }

  sendATASMS(
    id: number,
    temId: number,
    accNo: string
    // See C002 - 2026-08-20 - HE
    // ): Observable<BasicResponse> {
  ): Observable<AtaSmsResponse> {
    const headers = this.headers;
    return this.httpClient
      .post<AtaSmsResponse>(
        `${this.URL}/awt/sendatasms/${id}/${temId}/${accNo}`,
        0,
        { headers }
      )
      .pipe(catchError(AwaitingTransactionService.handleHTTPError));
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Added AwaitingTransactionService for MBengine_ENS ATA/AWT APIs. Previous behavior: No Angular client for /awt in this repo; issuing app used AwaitingTransactionService in a different services package. New behavior: URL is REST_SERVER_HOST + REST_SERVER_url MBE (/api/mbengine_ens). getAllAwaitingTrans POSTs JSON to /awt/get (not obsolete GET path params) after buildAwtRequest() defaults (pageSize 1000, status 0, memberNo/accNo '0') and local YYYY-MM-DD date filters. getAwaitingTrans GET /awt/get/{ataID}. sendATASMS POST /awt/sendatasms/{id}/{TEMID}/{accNo} with body 0. Reason: Duplicate ATA APIs for portaladmin; frontend out of scope. Context: ATA implementation guide 2026-08-20. Impact: providedIn root; uses existing MBE env entry (no new REST_SERVER_url key); interceptor still attaches Auth0 Bearer and appconsumer; no screens or routes. - 2026-08-20 - HE
// C002: sendATASMS return type changed from BasicResponse imported from '@auth-suite-services/services' to local AtaSmsResponse. Previous behavior: Service imported BasicResponse from the services barrel, which @nx/enforce-module-boundaries flagged as a circular dependency (mbe-service -> services -> suredebit-setup -> mbe-service). New behavior: Uses AtaSmsResponse in awaiting-transaction.ts with the same errors/status/summary shape. Reason: Remove the new circular import from the ATA client. Context: /build-and-lint 2026-08-20. Impact: Callers still receive the same JSON shape; no HTTP change. - 2026-08-20 - HE
